import { env } from './settings';
import { tasksPath, TaskAttributes } from './registry';
import { elasticLogEvent } from './ElasticLog';
import { PollMgrRegister } from './PollMgr';
import { getUnitWp, getRobotId, refreshedSnapshot } from './lib';
import { TASK_TYPE } from '../constants/Tasks';
import { getConfig } from '../utils/configUtils';
import { ONE_DAY_TICKS } from '../constants/DateTime';
import { arrLast } from './Utils';

export const securePt2Pt = 'point_to_point_delivery_w_robot_step_type_map_w_secure_goto';
const securePt2PtStepTypes = ['goto', 'load_tube', 'secure_goto', 'unload_tube'];

let nextSubscribeKey = 0;
let _snapshotHandlers = {};
const numRecords = 400; //over 4x our current single-site daily max => 106hrs@Denton
let loggedTimeWindow = false;

//provide a way to look up batch task records
export const batchLookup = {
  batchIdTable: {}, //batchId => [{task, lastSeen}]

  digest: (tasks) => {
    let now = Date.now();
    let hourAgo = now - 1000 * 60 * 60;
    const btab = batchLookup.batchIdTable;

    tasks.forEach((t) => {
      if (!t) return; //caution; shouldn't have to
      let batchId = t.task_data.batch_uuid;
      if (!batchId) return;
      let list = btab[batchId] || [];
      list = list.filter((entry) => entry.task.uuid != t.uuid); //kick out old if there
      list.push({ task: t, lastSeen: now });
      btab[batchId] = list;
    });

    //housekeep
    Object.keys(btab).forEach((batchId) => {
      let list = btab[batchId];
      list = list.filter((entry) => entry.lastSeen > hourAgo);
      if (list.length) btab[batchId] = list;
      else delete btab[batchId];
    });
  },

  getTasks: (batchId) => (batchLookup.batchIdTable[batchId] || []).map((e) => e.task),
};

export function getBatchTasks(batchId) {
  return batchLookup.getTasks(batchId);
}

export function getCurrentView(task, robotMap) {
  let canCancel = false,
    showNotes = false,
    restricted = !!task.task_data.restricted,
    stepType = '',
    stepIdx = 0,
    estopped = false;

  if (!task) {
    //do nothing
  }
  else if(task.task_type === securePt2Pt) {

    let start = task.task_level_args.start_goal.value;
    let end = task.task_level_args.end_goal.value;

    let batch = task.task_data.batch;
    let isSalient = false;
    if(batch?.batchType == 'oneToMany') {
      start = batch.stops[0];
      isSalient = batch.index == 0;
    }
    else if(batch?.batchType == 'manyToOne') {
      end = arrLast(batch.stops);
      isSalient = batch.index == batch.uuids.length - 1;
    }

    stepIdx = task.current_step ? task.steps.indexOf(task.current_step) : -1;
    stepType = securePt2PtStepTypes[stepIdx] || '';

    //if we're not the robot, show notes if relevant unit, and allow cancel if relevant unit and not past Load phase.
    if (!getRobotId()) {
      let unitId = getUnitWp();

      // unitId == task.requested_by //251215 removing requested_by from this logic 

      if(stepIdx <= 1) {
        if(unitId == start)
          canCancel = !batch || !isSalient || batch?.batchType == 'oneToMany';
        else if(unitId == end)
          canCancel = !batch || !isSalient || batch?.batchType == 'manyToOne';
      };

      showNotes =
        unitId == task.requested_by ||
        (unitId == start && !restricted) ||
        (unitId == end && !restricted);
    }

    Object.keys(robotMap || {}).some((robotId) => {
      let r = robotMap[robotId];
      if (r.current_task != task.uuid) return;
      if (!!r.aggregated_estop_state?.hardware_estop?.value) estopped = true;
      return true;
    });
  }
  else if (task.task_type == 'moxi_go_to_charger') {
    stepType = 'dock_with_charger';
  }

  return { canCancel, stepType, showNotes, stepIdx, restricted, estopped };
}

export function taskSubscribe(cb) {
  let key = nextSubscribeKey++;
  setSnapshotHandler(key, cb);
  return () => setSnapshotHandler(key, undefined);
}

//putting some often-used debugging code here in function form
function showRecentTasks(timeDescOrderDocs, numToShow) {
  if (numToShow) timeDescOrderDocs = timeDescOrderDocs.slice(0, numToShow);

  let dbData = timeDescOrderDocs.map((d) => {
    let data = d.data();
    let { claimed_by, requested_by, result, status, uuid, task_level_args, created, number_in_queue } = data;

    let start = task_level_args.start_goal.value;
    let end = task_level_args.end_goal.value;

    let date = new Date(created.seconds * 1000);
    let minutes = '' + date.getMinutes();
    if (minutes.length < 2) minutes = '0' + minutes;
    created = date.getHours() + ':' + minutes;
    return { claimed_by, requested_by, result, status, uuid, start, end, created, number_in_queue };
  });
  console.log(`\n----------------- ${dbData.length} tasks -----------------`);
  dbData.forEach((t) => console.log(JSON.stringify(t)));
}

/*
  Registers a handler which will be passed docs in descending time
  order.
*/
function setSnapshotHandler(key, handler) {
  if (handler) _snapshotHandlers[key] = handler; //overwrites allowed
  else {
    delete _snapshotHandlers[key];
  }
  PollMgrRegister('initTaskSubscription', initTaskSubscription);
}

function initTaskSubscription() {
  let tasksFunc = () =>
    env.firestore.collection(tasksPath(env.site)).orderBy(TaskAttributes.CREATED, 'desc').limit(numRecords);

  return refreshedSnapshot('tasks', tasksFunc, undefined, async (timeDescOrderResults) => {
    const nowTicks = Date.now();

    // timeDescOrderResults = {docs:[]}; //uncomment to simulate network-disrupted task stream

    //For observational purposes via web, note the total time window.
    let timeWindowStr = 'Task snapshots: no results';
    if (timeDescOrderResults.docs.length) {
      let d = timeDescOrderResults.docs;
      let earliestSeconds = d[d.length - 1].data().created.seconds; //nb: relies on desc time sorted order
      let hours = Math.floor((nowTicks / 1000 - earliestSeconds) / 360) / 10; //to 1 decimal place
      timeWindowStr = `Task snapshots: ${hours} hours`;
      if (!loggedTimeWindow) elasticLogEvent({ what: 'timeWindow', hours });
    }
    if (!loggedTimeWindow) console.log(timeWindowStr);
    loggedTimeWindow = true;

    //filter down to the past two days.  Six hours is probably
    //enough in a practical sense, but keeping a layer of conservativeness.
    //Also filter in only allowed template types.
    let cutoffSeconds = (nowTicks - 2 * ONE_DAY_TICKS) / 1000;
    let timeDescOrderData = [];
    for (let doc of timeDescOrderResults.docs) {
      let data = doc.data();
      if (data.created.seconds < cutoffSeconds) break;
      if ([securePt2Pt, 'dropoff', 'pickup'].indexOf(data.task_type) >= 0) timeDescOrderData.push(data);
    }

    // //simulate firestore not notifying us of transitions into load/unload
    //   xxx if uncomment, rework to use data rather than docs
    // let inProgIdx = timeDescOrderDocs.findIndex(t => t.data().status == 'in_progress');
    // if(inProgIdx > -1) {
    //   let d = timeDescOrderDocs[inProgIdx].data();
    //   if(d.current_step) {
    //     let idx = d.steps.indexOf(d.current_step);
    //     if(idx == 1 || idx == 3) {
    //       d.current_step = d.steps[idx - 1];
    //       timeDescOrderDocs[inProgIdx] = {exists:true, data:() => d};
    //     }
    //   }
    // }

    //maintain spanning lookup service
    batchLookup.digest(timeDescOrderData);

    //feed filtered docs to clients
    Object.keys(_snapshotHandlers).forEach((k) => _snapshotHandlers[k](timeDescOrderData));
  });
}
